import type { ChromecastUser } from '@tv2-player/unifiedplayer/src/types/ChromecastUser';
import JWT from '@tv2/lib-core/jwt';
import { GraphQLError } from 'graphql/error';
import type { CastReceiverContext } from '../../types/chromecast-caf-receiver/cast.framework';
import cast from '../../types/chromecast-caf-receiver';
import {
  playbackConfigFilter,
  playbackInfoHandlerFilter,
  errorAction,
  customMessageAction,
  beforeFetchMediaAction,
} from '../extensions';
import store, { DRM_JWT, JWT_IS_INVALID, USER_INFORMATION } from '../../store';
import getToken from '../../services/token';
import type { ChromecastPlayRequestCustomData, CustomCommands } from '../../types';
import { refreshAccessTokenIfExpired } from '../../utils/refreshAccessToken';
import { isErrorAInvalidJWTError } from '../../utils/graphQLUtils';
import fromUTF8 from '../../utils/string';
import fromBase64 from '../../utils/array';
import type { PluginInterface } from './PluginInterface';

export class DrmPlugin implements PluginInterface {
  init(_context: CastReceiverContext) {
    playbackConfigFilter.register(async playbackConfig => {
      // Always set baseline EME/shaka config; DRM specifics are added in applyDrmHandlers
      Object.assign(playbackConfig, {
        protectionSystem: cast.framework.ContentProtection.WIDEVINE,
      });
      return playbackConfig;
    });
    playbackInfoHandlerFilter.register(async (playbackConfig, loadRequest) => {
      const security = loadRequest.customData?.security;
      if (!security) return playbackConfig;

      // Reset invalid flag for each new load
      store.set(JWT_IS_INVALID, false);
      store.set(DRM_JWT, security.token);

      return {
        ...playbackConfig,
        licenseUrl: security.url,
        manifestRequestHandler: async () => {
          const isTokenValid = JWT.checkIfDRMTodayTokenValid(store.get(DRM_JWT));
          const stopFetching = store.get(JWT_IS_INVALID);
          if (isTokenValid === false && !stopFetching) {
            try {
              // Ensure access token is fresh before fetching DRM token
              const customData = loadRequest.customData as ChromecastPlayRequestCustomData;
              const user = store.get(USER_INFORMATION) as ChromecastUser | undefined;
              const maybeNewAccess = await refreshAccessTokenIfExpired(
                user?.accessToken,
                customData?.refreshToken
              );
              if (maybeNewAccess && user) {
                user.accessToken = maybeNewAccess;
              }

              const drm = await getToken(loadRequest.media.contentId, user as ChromecastUser);
              store.set(DRM_JWT, drm.token);
            } catch (err) {
              if (isErrorAInvalidJWTError(err as GraphQLError)) {
                store.set(JWT_IS_INVALID, true);
              }
              errorAction.perform(err);
            }
          }
        },
        licenseRequestHandler: requestInfo => {
          Object.assign(requestInfo.headers, {
            'x-dt-auth-token': store.get(DRM_JWT),
          });
        },
        licenseHandler: licenseData => {
          // This is the wrapped license, which is a JSON string.
          const wrappedString = fromUTF8(licenseData);
          // Parse the JSON string into an object.
          const wrapped = JSON.parse(wrappedString);
          // This is a base64-encoded version of the raw license.
          const rawLicenseBase64 = wrapped.license;
          // Decode that base64 string into a Uint8Array
          // The raw license will be fed to the Widevine CDM.
          return fromBase64(rawLicenseBase64);
        },
      };
    });
    // Listen for custom messages to update auth data at runtime
    customMessageAction.register(request => {
      const command = request.data.command as CustomCommands;
      if (command === 'REPLACE_ACCESS_TOKEN') {
        const newToken = request.data.accessToken ?? '';
        const user = store.get(USER_INFORMATION);
        if (user) {
          // Mutate the stored user object so all references (including PlaybackPlugin.user) see the update
          user.accessToken = newToken;
        }
      }

      if (command === 'SIMULATE_EXPIRED_DRM_TOKEN') {
        store.set(DRM_JWT, '');
      }
    });
    beforeFetchMediaAction.register(async loadRequest => {
      // Ensure access token is fresh before fetching DRM token
      const customData = loadRequest.customData as ChromecastPlayRequestCustomData;
      const user = store.get(USER_INFORMATION) as ChromecastUser | undefined;
      const maybeNewAccess = await refreshAccessTokenIfExpired(
        user?.accessToken,
        customData?.refreshToken
      );
      if (maybeNewAccess && user) {
        user.accessToken = maybeNewAccess;
      }
    });
  }
}
